import React, { useState } from 'react';
import { X, Trash2, AlertTriangle, FileX, HardDrive, Cloud, Search, CheckCircle, RefreshCw } from 'lucide-react';
import { analyzeFileSystem, executeCleanup, type FileCleanupReport, type CleanupAction } from '../utils/fileCleanupTool';

interface FileCleanupModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCleanupComplete: () => void;
}

const FileCleanupModal: React.FC<FileCleanupModalProps> = ({ isOpen, onClose, onCleanupComplete }) => {
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isExecutingCleanup, setIsExecutingCleanup] = useState(false);
  const [report, setReport] = useState<FileCleanupReport | null>(null);
  const [selectedActions, setSelectedActions] = useState<Set<number>>(new Set());
  const [cleanupResults, setCleanupResults] = useState<{
    successful: number;
    failed: number;
    errors: string[];
  } | null>(null);

  if (!isOpen) return null;

  const handleAnalyze = async () => {
    setIsAnalyzing(true);
    setReport(null);
    setCleanupResults(null);
    
    try {
      const analysisReport = await analyzeFileSystem();
      setReport(analysisReport);
      
      // Pre-select all safe cleanup actions
      const safeActionIndices = new Set<number>();
      analysisReport.cleanupActions.forEach((action, index) => {
        if (action.safe) {
          safeActionIndices.add(index);
        }
      });
      setSelectedActions(safeActionIndices);
    } catch (error) {
      console.error('[FileCleanup] Analysis failed:', error);
      alert(`Analysis failed: ${(error as Error).message}`);
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleExecuteCleanup = async () => {
    if (!report || selectedActions.size === 0) return;

    const actionsToExecute = report.cleanupActions.filter((_, index) => 
      selectedActions.has(index)
    );

    if (!window.confirm(`Are you sure you want to execute ${actionsToExecute.length} cleanup actions? This cannot be undone.`)) {
      return;
    }

    setIsExecutingCleanup(true);
    
    try {
      const results = await executeCleanup(actionsToExecute);
      setCleanupResults(results);
      
      if (results.successful > 0) {
        onCleanupComplete(); // Refresh the file list
      }
      
      if (results.failed === 0) {
        alert(`Cleanup successful! ${results.successful} files cleaned up.`);
      } else {
        alert(`Cleanup completed with issues:\n${results.successful} successful\n${results.failed} failed\n\nCheck the details below for more information.`);
      }
    } catch (error) {
      console.error('[FileCleanup] Cleanup failed:', error);
      alert(`Cleanup failed: ${(error as Error).message}`);
    } finally {
      setIsExecutingCleanup(false);
    }
  };

  const toggleAction = (index: number) => {
    const newSelected = new Set(selectedActions);
    if (newSelected.has(index)) {
      newSelected.delete(index);
    } else {
      newSelected.add(index);
    }
    setSelectedActions(newSelected);
  };

  const selectAllSafe = () => {
    if (!report) return;
    const safeIndices = new Set<number>();
    report.cleanupActions.forEach((action, index) => {
      if (action.safe) {
        safeIndices.add(index);
      }
    });
    setSelectedActions(safeIndices);
  };

  const selectNone = () => {
    setSelectedActions(new Set());
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-orange-100 rounded-lg">
              <FileX className="w-6 h-6 text-orange-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">File System Cleanup</h2>
              <p className="text-sm text-gray-600">Identify and remove duplicate or orphaned files</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
          {!report && !isAnalyzing && (
            <div className="text-center py-12">
              <div className="mx-auto w-16 h-16 bg-gradient-to-r from-orange-400 to-red-500 rounded-full flex items-center justify-center mb-6">
                <Search className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Analyze Your File System</h3>
              <p className="text-gray-600 mb-6 max-w-md mx-auto">
                This tool will scan all your files across cloud storage, local storage, and offline cache to identify duplicates, orphaned files, and other issues.
              </p>
              <button
                onClick={handleAnalyze}
                className="px-6 py-3 bg-gradient-to-r from-orange-600 to-red-600 text-white font-semibold rounded-lg hover:from-orange-700 hover:to-red-700 transition-all duration-200"
              >
                Start Analysis
              </button>
            </div>
          )}

          {isAnalyzing && (
            <div className="text-center py-12">
              <div className="animate-spin w-12 h-12 border-4 border-orange-500 border-t-transparent rounded-full mx-auto mb-4"></div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Analyzing File System...</h3>
              <p className="text-gray-600">This may take a moment as we scan all storage locations.</p>
            </div>
          )}

          {report && (
            <div className="space-y-6">
              {/* Summary */}
              <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl p-6 border border-blue-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Analysis Summary</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-orange-600">{report.summary.totalIssues}</div>
                    <div className="text-sm text-gray-600">Total Issues</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-green-600">{report.summary.safeDeletions}</div>
                    <div className="text-sm text-gray-600">Safe Deletions</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-yellow-600">{report.summary.requiresManualReview}</div>
                    <div className="text-sm text-gray-600">Manual Review</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-blue-600">{report.duplicateFiles.length}</div>
                    <div className="text-sm text-gray-600">Duplicate Groups</div>
                  </div>
                </div>
              </div>

              {/* Duplicate Files */}
              {report.duplicateFiles.length > 0 && (
                <div className="bg-white rounded-xl border border-gray-200 p-6">
                  <div className="flex items-center space-x-2 mb-4">
                    <AlertTriangle className="w-5 h-5 text-yellow-600" />
                    <h4 className="text-lg font-semibold text-gray-900">Duplicate Files ({report.duplicateFiles.length})</h4>
                  </div>
                  <div className="space-y-4">
                    {report.duplicateFiles.map((group, index) => (
                      <div key={index} className="border border-gray-200 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-3">
                          <div>
                            <h5 className="font-semibold text-gray-900">{group.name}</h5>
                            <p className="text-sm text-gray-600">{group.reason}</p>
                          </div>
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                            group.type === 'floorplan' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'
                          }`}>
                            {group.type === 'floorplan' ? 'Floor Plan' : 'Inventory'}
                          </span>
                        </div>
                        <div className="space-y-2">
                          {group.files.map((file, fileIndex) => (
                            <div key={fileIndex} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                              <div className="flex items-center space-x-3">
                                {file.source === 'supabase' ? (
                                  <Cloud className="w-4 h-4 text-blue-600" />
                                ) : (
                                  <HardDrive className="w-4 h-4 text-gray-600" />
                                )}
                                <div>
                                  <div className="text-sm font-medium text-gray-900">ID: {file.id}</div>
                                  <div className="text-xs text-gray-600">Source: {file.source}</div>
                                </div>
                              </div>
                              <div className="text-xs text-gray-500">
                                {file.updatedAt ? new Date(file.updatedAt).toLocaleDateString() : 'No date'}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Orphaned Files */}
              {report.orphanedFiles.length > 0 && (
                <div className="bg-white rounded-xl border border-gray-200 p-6">
                  <div className="flex items-center space-x-2 mb-4">
                    <FileX className="w-5 h-5 text-red-600" />
                    <h4 className="text-lg font-semibold text-gray-900">Orphaned Files ({report.orphanedFiles.length})</h4>
                  </div>
                  <div className="space-y-3">
                    {report.orphanedFiles.map((orphan, index) => (
                      <div key={index} className="flex items-center justify-between p-4 bg-red-50 border border-red-200 rounded-lg">
                        <div className="flex items-center space-x-3">
                          <div className={`p-2 rounded-lg ${orphan.canAutoDelete ? 'bg-red-100' : 'bg-yellow-100'}`}>
                            <FileX className={`w-4 h-4 ${orphan.canAutoDelete ? 'text-red-600' : 'text-yellow-600'}`} />
                          </div>
                          <div>
                            <div className="font-medium text-gray-900">ID: {orphan.id}</div>
                            <div className="text-sm text-gray-600">{orphan.issue}</div>
                            <div className="text-xs text-gray-500">Location: {orphan.location}</div>
                          </div>
                        </div>
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                          orphan.canAutoDelete 
                            ? 'bg-red-100 text-red-700' 
                            : 'bg-yellow-100 text-yellow-700'
                        }`}>
                          {orphan.canAutoDelete ? 'Auto-delete' : 'Manual review'}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Cleanup Actions */}
              {report.cleanupActions.length > 0 && (
                <div className="bg-white rounded-xl border border-gray-200 p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-2">
                      <Trash2 className="w-5 h-5 text-green-600" />
                      <h4 className="text-lg font-semibold text-gray-900">
                        Recommended Cleanup Actions ({selectedActions.size}/{report.cleanupActions.length} selected)
                      </h4>
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={selectAllSafe}
                        className="px-3 py-1 text-sm bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors"
                      >
                        Select All Safe
                      </button>
                      <button
                        onClick={selectNone}
                        className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                      >
                        Select None
                      </button>
                    </div>
                  </div>
                  
                  <div className="space-y-3 max-h-60 overflow-y-auto">
                    {report.cleanupActions.map((action, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center space-x-3">
                          <input
                            type="checkbox"
                            checked={selectedActions.has(index)}
                            onChange={() => toggleAction(index)}
                            className="w-4 h-4 text-green-600 rounded focus:ring-green-500"
                          />
                          <div className={`p-2 rounded-lg ${action.safe ? 'bg-green-100' : 'bg-yellow-100'}`}>
                            <Trash2 className={`w-4 h-4 ${action.safe ? 'text-green-600' : 'text-yellow-600'}`} />
                          </div>
                          <div>
                            <div className="font-medium text-gray-900">{action.description}</div>
                            <div className="text-sm text-gray-600">File: {action.fileName} (from {action.source})</div>
                          </div>
                        </div>
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                          action.safe 
                            ? 'bg-green-100 text-green-700' 
                            : 'bg-yellow-100 text-yellow-700'
                        }`}>
                          {action.safe ? 'Safe' : 'Review'}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Cleanup Results */}
              {cleanupResults && (
                <div className="bg-white rounded-xl border border-gray-200 p-6">
                  <div className="flex items-center space-x-2 mb-4">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    <h4 className="text-lg font-semibold text-gray-900">Cleanup Results</h4>
                  </div>
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div className="text-center p-4 bg-green-50 rounded-lg">
                      <div className="text-2xl font-bold text-green-600">{cleanupResults.successful}</div>
                      <div className="text-sm text-gray-600">Successful</div>
                    </div>
                    <div className="text-center p-4 bg-red-50 rounded-lg">
                      <div className="text-2xl font-bold text-red-600">{cleanupResults.failed}</div>
                      <div className="text-sm text-gray-600">Failed</div>
                    </div>
                  </div>
                  {cleanupResults.errors.length > 0 && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                      <h5 className="font-semibold text-red-900 mb-2">Errors:</h5>
                      <ul className="text-sm text-red-700 space-y-1">
                        {cleanupResults.errors.map((error, index) => (
                          <li key={index}>• {error}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        {report && !isExecutingCleanup && (
          <div className="flex items-center justify-between p-6 border-t border-gray-200 bg-gray-50">
            <button
              onClick={handleAnalyze}
              className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
            >
              Re-analyze
            </button>
            <div className="flex space-x-3">
              <button
                onClick={onClose}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
              >
                Close
              </button>
              {report.cleanupActions.length > 0 && (
                <button
                  onClick={handleExecuteCleanup}
                  disabled={selectedActions.size === 0}
                  className="px-6 py-2 bg-gradient-to-r from-red-600 to-orange-600 text-white font-semibold rounded-lg hover:from-red-700 hover:to-orange-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Execute Cleanup ({selectedActions.size})
                </button>
              )}
            </div>
          </div>
        )}

        {isExecutingCleanup && (
          <div className="flex items-center justify-center p-6 border-t border-gray-200 bg-gray-50">
            <div className="flex items-center space-x-3">
              <RefreshCw className="w-5 h-5 text-orange-600 animate-spin" />
              <span className="text-gray-700">Executing cleanup actions...</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FileCleanupModal; 